import React from 'react';

import { useSelect, UseSelectPropGetters } from 'downshift';
import { DismissableLayer } from '~/internal/DismissableLayer';
import { composeRefs } from '@radix-ui/react-compose-refs';
import { BulletFillIcon, CheckMarkOutlineIcon } from '@applyboard/ui-icons';
import { omitBy } from 'lodash';

import { useFullTheme } from '~/design';
import { assert } from '~/utils/assert';
import { findChildrenOfTypeRecursively, useSelectStyling } from '~/utils';
import { svgVerticalAlign } from '~/utils/styling';
import { useContextOrThrow } from '~/utils/useContextOrThrow';
import { FormControlContainer } from '~/internal/FormControlContainer';
import { IconWrapper } from '~/internal/IconWrapper';
import { Flex } from '~/components/Flex';
import { Text } from '~/components/Text';

import { Select, SelectOptionProps } from './Select';
import { SelectAppearanceProps } from './Select.utils';
import { ProgressIndicator } from '../ProgressIndicator';
import { getWidthStyles, useFloatingMenu } from '~/utils/floatingUi';
import { Portal } from '@radix-ui/react-portal';
import { Background } from '../../building-blocks/Background';

const SelectContext = React.createContext<{
  value: string;
  disabled: boolean;
  highlightedIndex: number;
  options: SelectOptionProps[];
  getItemProps: UseSelectPropGetters<SelectOptionProps>['getItemProps'];
} | null>(null);

export const StyledSelect = React.forwardRef<HTMLDivElement, SelectAppearanceProps>(
  function StyledSelect(
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      children,
      disabled = false,
      emphasis,
      helpText,
      iconOnClose,
      iconOnOpen,
      id,
      intent = 'secondary',
      invalid,
      label,
      labelPosition = 'top',
      menuAlignment,
      menuWidth,
      onBlur,
      onChange,
      placeholder = 'Select',
      placeholderVariant,
      required = false,
      size,
      value = '',
      loading,
      enforceNoMissingOption,
      width = 'fill',
    },
    ref,
  ) {
    const theme = useFullTheme();
    const { floatingStyles, refs } = useFloatingMenu({ menuAlignment, offset: 12 });

    const placeholderOption = placeholder ? <Select.Option label={placeholder} value="" /> : null;
    const options = findChildrenOfTypeRecursively(
      <>
        {placeholderOption}
        {children}
      </>,
      [Select.Option],
    ).map(c => c.props);

    const {
      isOpen,
      getToggleButtonProps,
      getLabelProps,
      getMenuProps,
      highlightedIndex,
      getItemProps,
      closeMenu,
      selectedItem,
    } = useSelect({
      items: options,
      itemToString: item => item?.label || '',
      selectedItem: value ? options.find(item => item.value === value) : null,
      stateReducer: (_, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        switch (type) {
          case useSelect.stateChangeTypes.ItemClick:
          case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
          case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
            changes.isOpen = false;
            if (!changes.selectedItem) changes.selectedItem = { label: '', value: '' };
        }
        return changes;
      },
      isItemDisabled(item) {
        return !!item?.disabled;
      },
      onSelectedItemChange: changes => onChange(changes.selectedItem?.value || ''),
    });

    const foundOption = !!selectedItem?.value;

    const selectedOption = options.find(item => item.value === value);
    const selectedOptionIntent = selectedOption?.intent;
    const SelectedOptionIcon = selectedOption?.icon;

    if (value && !selectedOption && enforceNoMissingOption) {
      throw new Error(`[Select] No option found with value "${value}"`);
    }

    const triggerLabel = foundOption ? (
      placeholderVariant === 'value' ? (
        <Flex gap={2}>
          {SelectedOptionIcon && <SelectedOptionIcon />}

          <span
            css={{
              minWidth: 0,
              overflowX: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedOption?.label}
          </span>
        </Flex>
      ) : (
        <Flex gap={1} grow={1} justify="between">
          {placeholder}
          <IconWrapper disabled={disabled} intent="primary" selector="div">
            <BulletFillIcon />
          </IconWrapper>
        </Flex>
      )
    ) : (
      placeholder
    );

    const labelProps = getLabelProps();
    const helpTextId = `${id}-helptext`;
    const adjustedIntent = disabled ? 'secondary' : invalid ? 'negative' : intent;
    const styles = useSelectStyling({
      emphasis,
      invalid,
      intent: adjustedIntent,
      size,
      selectedOptionIntent,
    });
    const IconTag = isOpen ? iconOnOpen : iconOnClose;

    const toggleButtonProps = getToggleButtonProps(
      omitBy(
        {
          ref: composeRefs(refs.setReference, ref),
          onBlur: onBlur ? () => onBlur() : undefined,
          'aria-labelledby': ariaLabelledBy,
          'aria-label': ariaLabel,
          'aria-describedby': helpTextId,
        },
        v => !v,
      ),
    );

    return (
      <SelectContext.Provider
        value={{
          value,
          highlightedIndex,
          options,
          getItemProps,
          disabled,
        }}
      >
        <FormControlContainer
          disabled={disabled}
          helpText={helpText}
          helpTextId={helpTextId}
          intent={intent}
          invalid={invalid}
          label={label}
          labelPosition={labelPosition}
          required={required}
          id={labelProps.htmlFor}
          labelId={labelProps.id}
          width={width}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- The div's role is set to combobox so this keyboard event is fine */}
          <div
            {...toggleButtonProps}
            onClick={event => {
              if (disabled) {
                return;
              }

              if (typeof toggleButtonProps.onClick === 'function') {
                // @ts-expect-error Not sure how to fix this.  It should be fine at runtime
                toggleButtonProps.onClick(event);
              }
            }}
            onKeyDown={event => {
              if (disabled) {
                return;
              }

              if (isOpen && (event.key === 'Enter' || event.key === ' ')) {
                // Simulate a click event on the active descendant, so the onClick
                // handler is called.  This is similar to what happens when you press
                // enter / space on a button
                const activeDescendent = event.currentTarget.getAttribute('aria-activedescendant');
                if (activeDescendent) {
                  const li = document.getElementById(activeDescendent);
                  if (li) {
                    li.click();
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                  }
                }
              }

              // Let DismissableLayer handle the Escape key press
              if (isOpen && event.key === 'Escape') {
                return;
              }

              if (typeof toggleButtonProps.onKeyDown === 'function') {
                // @ts-expect-error Not sure how to fix this.  It should be fine at runtime
                toggleButtonProps.onKeyDown(event);
              }
            }}
            css={[
              styles,
              {
                gap: placeholderVariant === 'value' || !foundOption ? theme.spacing[2] : 0,
              },
              svgVerticalAlign,
            ]}
            aria-disabled={disabled}
            data-__do_not_use__-cds-element="rounded-border"
          >
            {triggerLabel}
            {loading ? (
              <ProgressIndicator indeterminate aria-label="Loading" variant="circular" />
            ) : (
              <IconWrapper
                contrast="high"
                disabled={disabled}
                intent={adjustedIntent}
                selector="div"
              >
                <IconTag />
              </IconWrapper>
            )}
          </div>
        </FormControlContainer>
        <Portal asChild>
          <DismissableLayer
            onPointerDownOutside={e => e.preventDefault()}
            onFocusOutside={e => e.preventDefault()}
            onDismiss={closeMenu}
            enabled={isOpen}
          >
            <Background
              background="bgRegular"
              as="ul"
              {...getMenuProps({
                ref: refs.setFloating,
              })}
              css={{
                display: isOpen ? 'block' : 'none',
                margin: 0,
                paddingBlock: 0,
                paddingInline: 0,
                borderRadius: theme.borderRadiuses.container,
                boxShadow: theme.elevations.z1,
                minHeight: Math.min(
                  React.Children.count(React.Children.toArray(children).filter(Boolean)) * 48,
                  200,
                ),
                overflowX: 'auto',
                zIndex: theme.zIndex.menu,
                ...getWidthStyles(menuWidth, refs),
              }}
              style={{
                ...floatingStyles,
                pointerEvents: isOpen ? 'all' : 'none',
              }}
            >
              {isOpen && (
                <>
                  {placeholderOption}
                  {children}
                </>
              )}
            </Background>
          </DismissableLayer>
        </Portal>
      </SelectContext.Provider>
    );
  },
);

export const StyledOption = React.forwardRef<HTMLLIElement, SelectOptionProps>(
  function StyledOption(
    {
      disabled: optionDisabled,
      label,
      value,
      icon: OptionIcon,
      'aria-controls': ariaControls,
      'aria-haspopup': ariaHasPopup,
      onClick,
      intent,
    },
    ref,
  ) {
    const {
      options,
      highlightedIndex,
      value: selectedValue,
      disabled: allOptionsDisabled,
      getItemProps,
    } = useContextOrThrow(SelectContext);

    const index = options.findIndex(option => option.value === value);

    // If we render an option not found by the Select, then it causes keyboard
    // accessibility issues that violate https://www.w3.org/WAI/WCAG22/quickref/#keyboard.
    // The user can't use the keyboard to navigate to the option.
    //
    // Instead of silently allowing this, lets throw an error to force the dev
    // to fix the issue
    assert(index !== -1, 'Select.Option must be rendered in the same component as the Select');

    const isSelected = value === selectedValue;
    const isHighlighted = index === highlightedIndex;
    const disabled = optionDisabled || allOptionsDisabled;
    const optionIntent = intent || (isSelected ? 'primary' : 'secondary');
    const checkmarkIntent = isSelected ? 'primary' : 'secondary';

    const theme = useFullTheme();
    const baseStyles = {
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing[4],
      minHeight: '52px',
      padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
      listStyleType: 'none',
      backgroundColor: 'transparent',
      border: 0,
      borderRadius: 0,
    };

    const iconContrast = intent === 'negative' || intent === 'positive' ? 'mid' : 'high';
    const textContrast = disabled
      ? 'low'
      : intent === 'negative' || intent === 'positive'
        ? 'mid'
        : 'high';

    return (
      // eslint-disable-next-line jsx-a11y/role-supports-aria-props -- While `aria-haspopup` is deprecated on the `listitem` role, it seems to improve the SR experience.
      <li
        aria-controls={ariaControls}
        aria-haspopup={ariaHasPopup}
        {...getItemProps({
          index,
          item: options[index],
          onClick,
          ref,
        })}
        key={value}
        css={[
          baseStyles,
          !disabled && {
            '&:focus': {
              boxShadow: `inset ${theme.colors.actionPrimary} 0px 0px 0px 1px, ${theme.colors.actionPrimary} 0px 0px 0px 1px`,
              outline: 'none',
            },
            '&:hover': {
              backgroundColor: theme.colors.bgRegularHover,
            },
            '&:active': {
              backgroundColor: theme.colors.bgRegularPressed,
            },
          },
          isSelected &&
            !disabled && {
              backgroundColor: theme.colors.bgPrimary,
              '&:hover': {
                backgroundColor: theme.colors.bgPrimaryHover,
              },
              '&:active': {
                backgroundColor: theme.colors.bgPrimaryPressed,
              },
            },
          isSelected &&
            disabled && {
              backgroundColor: theme.colors.bgPrimaryHighlight,
            },
          isHighlighted &&
            !disabled && {
              backgroundColor: theme.colors.bgRegularHover,
            },
        ]}
      >
        {OptionIcon && (
          <IconWrapper
            contrast={iconContrast}
            selector="li"
            intent={optionIntent}
            disabled={disabled}
          >
            <OptionIcon />
          </IconWrapper>
        )}
        <Text variant="bodyM" contrast={textContrast} intent={optionIntent}>
          {label}
        </Text>
        {isSelected && (
          <span css={{ marginLeft: 'auto' }}>
            <IconWrapper
              contrast="high"
              selector="span"
              intent={checkmarkIntent}
              disabled={disabled}
            >
              <CheckMarkOutlineIcon />
            </IconWrapper>
          </span>
        )}
      </li>
    );
  },
);
